<script setup lang="ts">
import { computed, defineEmits, defineProps, onMounted, ref, watch } from "vue";
import type { ButtonState, ContentItem } from "@edco/apollo";
import { useRoute } from "#imports";
import ColorModeSwitcher from "~/components/layout/ColorModeSwitcher.vue";

type Link = { label: string; url: string };
const props = defineProps( {
  quickLinks: {
    type: Array as PropType<Link[]>,
    default: () => [],
  },
  addressLines: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
  linkedInUrl: {
    type: String,
    default: null,
  },
  twitterUrl: {
    type: String,
    default: null,
  },
  hasAcceptedGuidelines: {
    type: Boolean,
    // default: false,
    default: true,
  },
  guidelines: {
    type: Object as PropType<ContentItem>,
    default: null,
  },
  copyrightNotice: {
    type: String,
    default: null,
  },
  quickLinksHeading: {
    type: String,
    default: null,
  },
  footerHeading: {
    type: String,
    default: null,
  },
  footerDescription: {
    type: String,
    default: null,
  },
  socialLinksHeading: {
    type: String,
    default: null,
  },
} );

const emit = defineEmits( ["accepted-guidelines"] );

const currentYear = new Date().getFullYear();
const showGuidelines = ref( false );

const route = useRoute();
const memberResource = $isnMemberResource();
const auth = useAuth();

const state = ref<ButtonState>( null );

const headlessData = useHeadlessDataState();
const member = useMember();

const shouldShowGuidelines = computed( () => {
  if ( !auth.isLoggedIn.value ) {
    return false;
  }

  if ( member.value?.acceptedGuidelines ) {
    return false;
  }

  const ignoredRoutes = ["register", "linkSchools", "competitionArea"];
  if ( ignoredRoutes.includes( headlessData.value?.subType ) ) {
    return false;
  }

  return true;
} );

const copyrightNotice = computed( () => props.copyrightNotice?.replace( "{year}", currentYear.toString() ) );

watch(
  () => route.fullPath,
  () => {
    if ( shouldShowGuidelines.value ) {
      showGuidelinesModal();
    }
  },
);

// onMounted( () => {
//   if ( shouldShowGuidelines.value ) {
//     showGuidelinesModal();
//   }
// } );

watch( state, ( val ) => {
  const watchedStates: ButtonState[] = ["success", "failure"];
  if ( watchedStates.includes( val ) ) {
    setTimeout( () => {
      state.value = null;
    }, 2000 );
  }
} );

function showGuidelinesModal () {
  showGuidelines.value = false;
}

async function acceptCommunityGuidelines () {
  if ( state.value !== "loading" ) {
    state.value = "loading";

    try {
      await memberResource.acceptCommunityGuidelines();
      state.value = "success";
      emit( "accepted-guidelines" );
      showGuidelines.value = false;
    } catch ( err ) {
      state.value = "failure";
    }
  }
}

function onScrollUp ( link: Link ) {
  if ( link.url === route.path ) {
    window.scrollTo( { top: 0, behavior: "smooth" } );
  }
}
</script>

<template>
  <footer class="site-footer">
    <div class="container site-footer__content">
      <h2>{{ footerHeading || "Building better education" }}</h2>
      <p style="margin-bottom: 50px;">
        {{ footerDescription || "Contact us to learn how we can work together to improve our education systems" }}
      </p>
      <div class="row">
        <div class="col-xs-12 col-sm-12 col-md-4  ">
          <div v-if="quickLinks.length || guidelines">
            <template v-if="quickLinks.length">
              <p><span class="site-footer__heading">{{ quickLinksHeading || "Learn more" }}</span></p>
              <div
                class="2-cols"
                style="display: flex; gap: 40px;">
                <ul class="links">
                  <li
                    v-for="(link, index) in quickLinks"
                    :key="index">
                    <nuxt-link
                      :to="link.url"
                      @click="onScrollUp(link)">
                      {{ link.label }}
                    </nuxt-link>
                  </li>
                </ul>
                <color-mode-switcher />
              </div>
            </template>
          </div>
        </div>

        <!-- <div
          v-if="addressLines && addressLines.length"
          class="col-xs-12 col-sm-12 col-md-3">
          <span class="site-footer__heading">Contact Us</span>

          <address class="address">
            <span
              v-for="(line, index) in addressLines"
              :key="index">
              {{ line }}
            </span>
          </address>
        </div> -->

        <div class="col-xs-12 col-sm-12 col-md-4">
          <div
            v-if="linkedInUrl || twitterUrl"
            class="">
            <div>
              <span class="site-footer__heading">{{ socialLinksHeading || "Follow Us" }}</span>

              <links-list
                :linked-in-name="linkedInUrl"
                :twitter-name="twitterUrl"
                show-platform-names />
            </div>
          </div>
        </div>

        <div class="col-xs-12 col-sm-12 col-md-4" />

        <div class="col-xs-12 col-sm-12 col-md-6" />
      </div>
    </div>

    <div
      v-if="copyrightNotice"
      class="site-footer__copyright">
      <div class="container">
        &copy; {{ copyrightNotice }}
      </div>
    </div>

    <!-- Guidelines Modal -->
    <AModal
      v-if="guidelines"
      v-model="showGuidelines"
      size="lg">
      <ACard>
        <template #header>
          <AModalTitle>
            {{ guidelines.name }}
          </AModalTitle>
        </template>

        <template #default>
          <rich-text
            v-if="guidelines"
            :content="guidelines.properties.content" />

          <p v-else>
            Error loading guidelines. Please try again later.
          </p>
        </template>

        <template #footer>
          <a-button
            v-if="auth.isLoggedIn.value"
            v-model="state"
            look="pill"
            :disabled="hasAcceptedGuidelines"
            :prefix="hasAcceptedGuidelines ? 'fa:regular:check' : ''"
            :suffix="hasAcceptedGuidelines ? '' : 'fa:light:chevron-right'"
            @click="acceptCommunityGuidelines">
            <template v-if="hasAcceptedGuidelines">
              Accepted
            </template>
            <template v-else>
              Agree &amp; continue
            </template>
          </a-button>
          <p v-else>
            You must be logged in to agree to the community guidelines.
          </p>
        </template>
      </ACard>
    </AModal>
  </footer>
</template>

<style lang="scss" scoped>
.site-footer {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 60px 0 0px 0;
  background-color: #201d25;
  min-height: 200px;
  color: #fff;
  margin-top: 30px;
  font-size: 1rem;

  &__content:last-child {
    padding-bottom: 40px;
  }

  &__heading {
    display: block;
    color: var(--primary-color);
    font-size: 1.1rem;
    margin-bottom: 10px;
    font-family: var(--font-family-base), sans-serif;
    font-weight: 600;
  }

  :deep( a ) {
    color: #fff !important;

    &:hover {
      color: var(--primary-color) !important;
    }
  }

  :deep( dd ) {
    margin-bottom: 0;
  }

  .links {
    padding-left: 0px;
    margin: 0 0 10px 0;;
    font-size: 1rem;
    list-style-type: none;

    a {
      text-decoration: none;
      display: inline-block;
      padding: 0 0 5px 0;
    }

  }

  .address {
    display: flex;
    flex-direction: column;
  }

  &__copyright {
    margin-top: 30px;
    background: #000;
    padding: 25px 0;
    margin-bottom: 0;
  }

  h2 {
    color: #f1f1f1;
    font-weight: 500;
    font-size: 2.4rem;
    margin:0 0 10px 0;
    padding:0;
  }

  @media (max-width: 340px) {
    .col-4 {
      flex-basis: 50%;
      max-width: 50%;
      margin-bottom: 15px;
    }
  }

  @media (max-width: 768px) {
    .color-mode-switcher {
      margin-top: 15px;
    }
  }
}

.site-footer__content {
  width: 100%;
  max-width: var(--container-max-width);
  padding-bottom: 20px;
}

.btn-guidelines {
  padding: 0;
  color: #fff;
  font-size: 0.8rem;
  gap: 5px;
  display: none;

  svg {
    color: var(--primary-color);
  }

  &:hover {
    color: var(--primary-color);
  }
}

.links + .btn-guidelines {
  margin-top: 10px;
}

.cookie-consent-wrapper {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 299;
  background: var(--bg-color);
}

.cookie-consent {
  display: flex;
  flex-wrap: wrap;
  padding: 10px;
  align-items: center;
  align-self: center;
  justify-content: flex-start;
  color: var(--secondary-color);

  .btn {
    margin: 10px 0;
  }
}

:deep( .cookie-consent-link ) {
  padding: 0 10px;
}

.dismiss-consent {
  background: none;
  border: 0;
  color: var(--secondary-color);

  &:hover {
    color: var(--primary-color);
  }
}

.color-mode-switcher {
  margin-top: 40px;
}
</style>
